/**
 * Where everything on the training course stands (ROUND7 §5, 08 §4 "interactive field manual"). One table,
 * read by the course builder, by the coach that talks the trainee through it and by the tests that walk it,
 * so what the coach says is always where it says it.
 *
 * The strip runs south to north like every mission: he lands in the south and works north, one station at
 * a time, to the X. The distances are the lesson's, not decoration:
 *
 *   - the pod clearing is north of the drop band, where a pod kicked just before the green light comes down;
 *   - both knife sentries face NORTH, away from him as he comes up the lane;
 *   - the watcher faces SOUTH, down the lane; the trench mouth is outside his 22 m of daylight sight and the
 *     trench runs on past him, so the only unseen way by is to crawl it;
 *   - the dog team works a beat north of the trench, and a stream with no ford runs edge to edge north of it
 *     (ROUND8 §5): nothing south of the stream can ever cross it, and all of that ground is out of earshot of
 *     the range and the wall;
 *   - the firing line is more than `CARBINE.noise` (45 m) from the watcher and the grenade crates more than
 *     `GRENADE.noise` (70 m); the LAW spot and the wall more than `LAW.noise` / `BOMB.noise` (100 m). Nobody
 *     alive hears the range or the wall, so the garrison alert never rises there (02 §6.3).
 *
 * Coordinates: metres, +x east, +z SOUTH (AGENT-RULES 10). Pure data.
 */

import { HALF_PI, PI } from '../../sim/math';

export interface Point {
  x: number;
  z: number;
}

/** A soldier's post and which way he looks: 0 = south, PI = north. */
export interface Post extends Point {
  facing: number;
}

const NORTH = PI;
const SOUTH = 0;
const EAST = HALF_PI;

/**
 * The stream's centre line, west edge to east edge and a little past both, with a gentle wander so it reads as
 * a stream and not a ditch. Its water spans `STREAM_WIDTH` about this line.
 */
const STREAM_LINE: ReadonlyArray<readonly [number, number]> = [
  [-6, 150.4], [8, 149.5], [22, 150.6], [36, 149.4], [50, 150.5], [70, 149.7],
];
const STREAM_WIDTH = 4;
const streamZs = STREAM_LINE.map(([, z]) => z);

export const COURSE = {
  sizeX: 64,
  sizeZ: 440,
  /** The jump light is green over this band; past `z1 + 6` is the badlands (a scrub). */
  dropBand: { z0: 340, z1: 430 },
  /** Where he stands when the drop is skipped (tests); in the band, on the lane. */
  start: { x: 32, z: 390 },
  /** Open ground north of the band: a pod kicked while the aircraft is over it lands on the lane. */
  podClearing: { z0: 304, z1: 340 },
  /** The lane up the middle of the course: nothing stands on it, so a kicked pod lands whole. */
  laneX: 32,
  /**
   * A light wind from the north, m/s (+z is south): enough for the HUD's wind arrow (ROUND8 §4) and for the dog
   * lesson - coming up the course from the south he is downwind of everything ahead of him - and too little to
   * spoil a drop: it carries a pod about 4 m and the canopy about 5 m further south.
   */
  wind: { x: 0, z: 0.6 },

  /** Station 3: the knife. `a` is on the lane; `b`, off to the west, is the second chance. */
  knife: {
    a: { x: 32, z: 286, facing: NORTH } as Post,
    b: { x: 12, z: 268, facing: NORTH } as Post,
    /** North of this the knife station is behind him. */
    passedZ: 256,
  },

  /** Station 4: being seen. The watcher, and the trench that takes a crawler past him unseen. */
  watcher: { x: 28, z: 212, facing: SOUTH } as Post,
  trench: { x: 40, z0: 204, z1: 234, width: 2.2, depth: 1.3 },

  /**
   * Station 5: the dog (ROUND8 §5). An unarmed handler walks a beat across the course with his dog at heel, 10 m
   * north of the trench's end: lying in the end of the trench, unseen, the trainee has the team on screen as it
   * crosses the lane (trainingVisible.test.ts), and can wait for its back and cross behind it - south of the dog,
   * downwind of it, where its nose is shortest. Where he crossed the beat the dog finds his trail and runs it
   * north, 40 m to the stream; wading the stream wipes it (hazards/tracks.ts). The stream has no ford: soldiers do
   * not wade (hazards/terrain.ts), so neither the dog nor its handler can follow him over it, whatever happens
   * south of it.
   */
  dogs: {
    handler: { x: 12, z: 194, facing: EAST } as Post,
    /** His beat, walked there and back for ever. */
    beat: [[12, 194], [52, 194]] as ReadonlyArray<readonly [number, number]>,
    stream: {
      points: STREAM_LINE,
      width: STREAM_WIDTH,
      /** The water's north and south edges, at their furthest: south of `south` is dry, north of `north` too. */
      north: Math.min(...streamZs) - STREAM_WIDTH / 2,
      south: Math.max(...streamZs) + STREAM_WIDTH / 2,
    },
  },

  /**
   * Station 6: the range. He fires from `firingPoint`, over the sandbags just north of it. Everything he is to
   * hit stands within about 6 m north of him: the picture reaches only ~14 m north of the man it centres on,
   * less under the HUD panels, and a lamp is drawn 3 m further north again. The first layout put the lamps
   * 18-20 m out - off the top of the screen from the spot the coach sends him to (found in play, 2026-09-23).
   */
  range: {
    firingPoint: { x: 36, z: 80 },
    sandbags: { x: 36, z: 77.5, length: 4 },
    /** Searchlight poles, unlit by day: a carbine round puts the lamp out (02 §9.2). */
    lamps: [{ x: 27, z: 74 }, { x: 30.5, z: 74.5 }, { x: 24, z: 75 }] as readonly Point[],
    /** A stack of crates for the grenade: low, so a throw carries over them and lands among them. */
    crates: [{ x: 44, z: 75 }, { x: 45.2, z: 75.4 }, { x: 44.4, z: 76.6 }] as readonly Point[],
  },

  /** Station 7: the practice wall, an east-west run. The middle section is the objective. */
  wall: {
    z: 36,
    x0: 26,
    x1: 38,
    /** The marked section in the middle: 3 m, so a LAW anywhere near the middle takes it. */
    targetX0: 30.5,
    targetX1: 33.5,
    /**
     * Where to stand for the LAW: 10 m back. The rocket must FLY 6 m to arm, and it leaves the tube 0.8 m ahead
     * and strikes a wall its width short of the face, so 7 m back was a dud (found playing the course through
     * real inputs, 2026-09-23); 10 m clears that and the 5 m of its blast, and keeps the wall on screen.
     */
    lawSpot: { x: 32, z: 46 },
  },

  /** Station 8: the X. */
  pickup: { x: 32, z: 16 },
  goalZone: { x0: 16, z0: 4, x1: 48, z1: 52 },
  clockSeconds: 3600,
} as const;

/** Prop ids, fixed so the coach and the tests can name them. */
export const COURSE_IDS = {
  /** The marked middle section of the wall; the pieces either side are numbered from 100 (west) and 110 (east). */
  wallTarget: 120,
  wallWest: 100,
  wallEast: 110,
  lamps: [200, 201, 202] as readonly number[],
  crates: [210, 211, 212] as readonly number[],
  sandbags: 220,
} as const;
